import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { AppThunk } from 'store/store'
import { Pagination } from './Types/InventoryType'
import {
  EstoqueDisponivelMaterial,
  EstoqueFabrica,
  RequestInventoryMaterials,
  RequestInventoryTotalizer
} from 'services/draft/interfaces'
import {
  getInventoryMaterials,
  getInventoryTotalizer,
  getInventoryReport
} from 'services/draft'
import { isObjectEmpty } from 'commons/utils/object'
import { formatDate } from 'commons/utils/date'

type InitialState = {
  loadingInventory: boolean
  loadingInventoryReport: boolean
  materialsList: EstoqueDisponivelMaterial[]
  inventoryReport: ArrayBuffer
  totalizer: EstoqueFabrica[]
  pagination: Pagination
  lastUpdate: string
}
const initialState: InitialState = {
  loadingInventory: true,
  loadingInventoryReport: false,
  materialsList: [],
  inventoryReport: {} as ArrayBuffer,
  totalizer: [],
  lastUpdate: '',
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0
  }
}

const inventory = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    onRequest(state) {
      state.loadingInventory = true
    },
    afterRequest(state) {
      state.loadingInventory = false
    },
    setReportGenerateLoading(state, action: PayloadAction<boolean>) {
      state.loadingInventoryReport = action.payload
    },
    setMaterialList(
      state,
      action: PayloadAction<EstoqueDisponivelMaterial[] | undefined>
    ) {
      if (action.payload) {
        state.materialsList = action.payload
      } else {
        state.materialsList = []
      }
    },
    setTotalizer(state, action: PayloadAction<EstoqueFabrica[]>) {
      state.totalizer = action.payload
    },
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload
    },
    setLastUpdate(state, action: PayloadAction<string>) {
      state.lastUpdate = action.payload
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload
    },
    resetPagination(state) {
      state.pagination = {
        currentPage: 1,
        pageSize: 20,
        totalItems: 0,
        totalPages: 0
      }
    },
    setInventoryReport(state, action: PayloadAction<ArrayBuffer>) {
      if (action.payload) {
        state.inventoryReport = action.payload
      }
    }
  }
})

export const inventoryContext = inventory.reducer
export const {
  onRequest,
  afterRequest,
  setMaterialList,
  setTotalizer,
  setPagination,
  setLastUpdate,
  setCurrentPage,
  resetPagination,
  setInventoryReport,
  setReportGenerateLoading
} = inventory.actions

export const asyncGetInventoryTotalizer =
  (payload: RequestInventoryTotalizer): AppThunk =>
  async (dispatch) => {
    dispatch(onRequest())
    try {
      const totalizer = await getInventoryTotalizer(payload)
      dispatch(setTotalizer(totalizer))
    } catch (error) {
      toast.error('Erro ao obter totalizadores.')
    } finally {
      dispatch(afterRequest())
    }
  }

export const asyncGenerateInventoryReport = (): AppThunk => async (dispatch) => {
  dispatch(setReportGenerateLoading(true))

  try {
    const report = await getInventoryReport()

    if (!isObjectEmpty(report) || report.byteLength > 0) {
      // TODO: Replace with utils/downlodFile({file, fileName, fileType})
      const url = window.URL.createObjectURL(new Blob([report]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio_estoque.xlsx`)
      link.click()

      toast.success('Relatório baixado com sucesso!')
    }
  } catch (error) {
    toast.error('Não foi possível baixar o relatório. Tente novamente mais tarde.')
  } finally {
    dispatch(setReportGenerateLoading(false))
  }
}

export const asyncGeInventoryMaterial =
  (payload: RequestInventoryMaterials): AppThunk =>
  async (dispatch, getState) => {
    dispatch(onRequest())
    const { pagination } = getState().inventory

    try {
      const materialsData = await getInventoryMaterials(payload)

      if (materialsData.length > 0) {
        dispatch(
          setPagination({
            ...pagination,
            currentPage: payload.numeroPaginas ? payload.numeroPaginas + 1 : 1,
            totalItems: materialsData[0].TotalRegistros,
            totalPages: Math.ceil(materialsData[0].TotalRegistros / pagination.pageSize)
          })
        )
        dispatch(setLastUpdate(formatDate(new Date(), 'HH:mm')))
        dispatch(setMaterialList(materialsData))
      } else {
        dispatch(setMaterialList([]))
        dispatch(
          setPagination({
            ...pagination,
            currentPage: payload.numeroPaginas ? payload.numeroPaginas + 1 : 1,
            totalItems: 0,
            totalPages: 0
          })
        )
      }
    } catch (error) {
      toast.error('Erro ao obter materiais.')
    } finally {
      dispatch(afterRequest())
    }
  }
export const asyncGeInventoryData =
  (payload: RequestInventoryMaterials): AppThunk =>
  async (dispatch, getState) => {
    dispatch(onRequest())
    const { pagination } = getState().inventory

    try {
      const materialsData = await getInventoryMaterials(payload)
      if (materialsData.length > 0) {
        dispatch(
          setPagination({
            ...pagination,
            currentPage: payload.numeroPaginas ? payload.numeroPaginas + 1 : 1,
            totalItems: materialsData[0].TotalRegistros,
            totalPages: Math.ceil(materialsData[0].TotalRegistros / pagination.pageSize)
          })
        )
        dispatch(setLastUpdate(formatDate(new Date(), 'HH:mm')))
        dispatch(setMaterialList(materialsData))
      } else {
        dispatch(setMaterialList([]))
        dispatch(
          setPagination({
            ...pagination,
            currentPage: payload.numeroPaginas ? payload.numeroPaginas + 1 : 1,
            totalItems: 0,
            totalPages: 0
          })
        )
      }
      const { caixaGrade, filtroMaterial, materialEstrategico, statusMaterial } = payload
      const totalizer = await getInventoryTotalizer({
        caixaGrade,
        filtroMaterial,
        materialEstrategico,
        statusMaterial
      })
      dispatch(setTotalizer(totalizer))
    } catch (error) {
      toast.error('Erro ao obter dados de estoque.')
    } finally {
      dispatch(afterRequest())
    }
  }
